import { DietaryCategory } from "@/types/product";
import { Product } from "@/types/product";
import { Variation } from "@/types/product";

export const getFieldValueToPopulate = ({
  originalProduct,
  previousProduct,
  fieldsToPopulate,
}: {
  originalProduct: Product | null;
  previousProduct: Product | null;
  fieldsToPopulate: (keyof Product)[];
}) => {
  return fieldsToPopulate.reduce((acc: Partial<Product>, field) => {
    if (Array.isArray(previousProduct?.[field])) {
      acc[field] =
        previousProduct?.[field]?.length > 0
          ? previousProduct?.[field]
          : originalProduct?.[field];
      return acc;
    }
    acc[field] = previousProduct?.[field] || originalProduct?.[field];
    return acc;
  }, {} as Partial<Product>);
};

export const getProductDietaryCategories = ({
  allDietaryCategories = [],
  productDietaryCategoryIds = [],
}: {
  allDietaryCategories: DietaryCategory[];
  productDietaryCategoryIds: number[];
}) => {
  return allDietaryCategories?.reduce((acc, category) => {
    if (productDietaryCategoryIds?.includes(category?.id)) {
      acc.push(category);
    }
    return acc;
  }, [] as DietaryCategory[]);
};

export const getCheapestVariant = (variants: Variation[]) => {
  if (!variants || variants.length === 0) return null;
  return variants.reduce((cheapest, current) => {
    const currentPrice = Number(cheapest.price_amount || 0);
    const currentPriceAmount = Number(current.price_amount || 0);
    return currentPriceAmount < currentPrice ? current : cheapest;
  });
};

// Helper function to generate initial form data from product
export const getInitialFormData = (product: Product) => {
  if (!product) {
    return {
      product_id: null,
      name: null,
      vendor_id: null,
      vendor_name: null,
      prod_img: null,
      isEggless: false,
      selectedVariant: null,
      count: 1,
      customizations: [],
      specialInstructions: "",
      selectedMessage: "",
      selectedFlavor: null,
    };
  }

  const initialSelectedVariant = getCheapestVariant(product.weight_variants || []);

  // For theme cakes, get initial flavor from the cheapest variant
  let initialFlavor = null;
  if (product?.isThemeCake && initialSelectedVariant?.flavour) {
    initialFlavor = {
      name: initialSelectedVariant.flavour.name,
      id: initialSelectedVariant.flavour.id,
    };
  } else if (product?.primary_flavor_details) {
    // For non-theme cakes, use primary flavor details
    initialFlavor = {
      name: product.primary_flavor_details.name,
      id: product.primary_flavor_details.id,
    };
  }

  const prodImg = product?.product_urls?.[0] || null;

  return {
    product_id: product.id,
    name: product.name,
    vendor_id: product.vendor,
    vendor_name: product.vendor_details?.business_name || "Unknown Vendor",
    prod_img: prodImg,
    isEggless: false,
    selectedVariant: initialSelectedVariant,
    count: 1,
    customizations: [],
    specialInstructions: "",
    selectedMessage: "",
    selectedFlavor: initialFlavor,
  };
};

// Helper function to get available weight variants for a specific flavor
export const getWeightVariantsForFlavor = (
  product: Product,
  flavorId: string | number,
) => {
  if (!product?.weight_variants || !flavorId) return [];

  return product.weight_variants
    .filter((variant: Variation) => variant.flavour?.id === flavorId)
    .sort((a: Variation, b: Variation) => Number(a.weight || 0) - Number(b.weight || 0));
};

// Helper function to get available flavors from product
export const getAvailableFlavors = (product: Product) => {
  // For theme cakes, get unique flavors from weight variants
  if (product?.isThemeCake && (product?.weight_variants?.length || 0) > 0) {
    const uniqueFlavors = new Map();

    product.weight_variants?.forEach((variant: Variation) => {
      if (variant.flavour && variant.flavour.id && variant.flavour.name) {
        uniqueFlavors.set(variant.flavour.id, {
          name: variant.flavour.name,
          id: variant.flavour.id,
        });
      }
    });

    return Array.from(uniqueFlavors.values());
  }

  // For non-theme cakes, use primary and secondary flavors
  const availableFlavors = [];
  if (product?.primary_flavor_details) {
    availableFlavors.push({
      name: product.primary_flavor_details.name,
      id: product.primary_flavor_details.id,
    });
  }
  if (product?.secondary_flavor_details) {
    availableFlavors.push({
      name: product.secondary_flavor_details.name,
      id: product.secondary_flavor_details.id,
    });
  }
  return availableFlavors;
};

// Helper function to calculate price based on form data and product
export const calculatePrice = (formData: { selectedVariant?: Variation | null; selectedFlavor?: { id: string | number; name: string } | null; isEggless?: boolean; customizations: { price_addition?: string | number }[]; count: number }, product: Product) => {
  let basePrice = 0;
  if (formData.selectedVariant) {
    // For theme cakes, find the variant that matches the selected flavor and weight
    if (
      product?.isThemeCake &&
      formData.selectedFlavor &&
      formData.selectedVariant
    ) {
      const matchingVariant = product.weight_variants?.find(
        (variant: Variation) =>
          variant.weight === formData.selectedVariant?.weight &&
          variant.flavour?.id === formData.selectedFlavor?.id,
      );

      if (matchingVariant) {
        basePrice = formData.isEggless
          ? Number(matchingVariant.eggless_price_amount || 0)
          : Number(matchingVariant.price_amount || 0);
      } else {
        // Fallback to selected variant price if no matching flavor variant found
        basePrice = formData.isEggless
          ? Number(formData.selectedVariant.eggless_price_amount || 0)
          : Number(formData.selectedVariant.price_amount || 0);
      }
    } else {
      // For non-theme cakes, use the selected variant price directly
      basePrice = formData.isEggless
        ? Number(formData.selectedVariant.eggless_price_amount || 0)
        : Number(formData.selectedVariant.price_amount || 0);
    }
  } else if (product?.weight_variants?.length === 0) {
    basePrice = Number(product.base_price_amount || 0);
  }

  const customizationsTotal = formData.customizations.reduce(
    (sum: number, option: { price_addition?: string | number }) =>
      sum + Number(option.price_addition || 0),
    0,
  );

  const subtotal = (basePrice + customizationsTotal) * formData.count;
  return subtotal;
};
